export type PushSubscriptionRequest = {
  endpoint: string;
  p256dh: string;
  auth: string;
};

export async function isAvailable(): Promise<boolean> {
  const worker = await getWorker();
  if (!worker || !worker.pushManager) {
    console.warn('Service worker or pushManager not available.');
    return false;
  }
  return true;
}

export async function isSubscribed(): Promise<boolean> {
  const worker = await getWorker();
  if (!worker || !worker.pushManager) {
    console.warn('Service worker or pushManager not available.');
    return false;
  }
  const existingSubscription = await worker.pushManager.getSubscription();
  return existingSubscription !== null;
}

export async function requestSubscription(): Promise<PushSubscriptionRequest | null> {
  const worker = await getWorker();
  if (!worker || !worker.pushManager) {
    console.warn('Service worker or pushManager not available.');
    return null;
  }

  const subscription = (await worker.pushManager.getSubscription()) ?? (await subscribe(worker));
  if (!subscription) {
    return null;
  }

  return {
    endpoint: subscription.endpoint,
    p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
    auth: arrayBufferToBase64(subscription.getKey('auth')),
  };
}

export async function requestUnsubscribe(): Promise<string | null> {
  const worker = await getWorker();
  if (!worker || !worker.pushManager) {
    console.warn('Service worker or pushManager not available.');
    return null;
  }

  const existingSubscription = await worker.pushManager.getSubscription();
  if (existingSubscription) {
    const endpoint = existingSubscription.endpoint;
    await unsubscribe(existingSubscription);
    return endpoint;
  }

  return null;
}

async function getWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers are not supported.');
    return null;
  }
  try {
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await navigator.serviceWorker.register('/service-worker.js');
    }

    await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.error('Failed to register service worker or wait for readiness:', error);
    return null;
  }
}

async function subscribe(worker: ServiceWorkerRegistration): Promise<PushSubscription | null> {
  try {
    const response = await fetch('/appsettings.json');
    const data = (await response.json()) as { VapidPublicKey: string };
    const vapidPublicKey = data.VapidPublicKey;
    return await worker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'NotAllowedError') {
      return null;
    }
    throw error;
  }
}

async function unsubscribe(subscription: PushSubscription): Promise<boolean | null> {
  try {
    return await subscription.unsubscribe();
  } catch (error) {
    if (error instanceof Error && error.name === 'NotAllowedError') {
      return null;
    }
    throw error;
  }
}

function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) {
    return '';
  }
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i] as number);
  }
  return window.btoa(binary);
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
